import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getUserProfile } from '../api/api';

const UserPreferencesContext = createContext(null);

const STORAGE_KEY = 'user:prefs';
const defaultPrefs = {
  timezone: 'UTC',
  locale: 'en-US',
  currency: 'USD',
  defaultAccountId: null,
};

function parseDateLike(value) {
  if (value == null) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'number') {
    const ms = value > 1000000000000 ? value : value * 1000; // allow seconds or ms
    return new Date(ms);
  }
  if (typeof value === 'string') {
    const ms = Date.parse(value);
    if (!Number.isNaN(ms)) return new Date(ms);
  }
  return null;
}

export function UserPreferencesProvider({ children }) {
  const [prefs, setPrefs] = useState(() => {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      const parsed = raw ? JSON.parse(raw) : {};
      return { ...defaultPrefs, ...parsed };
    } catch {
      return defaultPrefs;
    }
  });
  const [loaded, setLoaded] = useState(false);

  const persist = useCallback((next) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(next)); } catch { void 0; }
  }, []);

  useEffect(() => {
    let alive = true;
    const init = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) { setLoaded(true); return; }
        const profile = await getUserProfile();
        if (!alive) return;
        const next = {
          timezone: profile?.timezone || prefs.timezone || defaultPrefs.timezone,
          locale: profile?.locale || prefs.locale || defaultPrefs.locale,
          currency: (profile?.profile_currency || prefs.currency || defaultPrefs.currency || '').toString().toUpperCase(),
          defaultAccountId: profile?.default_account ?? prefs.defaultAccountId ?? defaultPrefs.defaultAccountId,
        };
        setPrefs(next);
        persist(next);
      } catch {
        // ignore and keep local prefs
      } finally {
        if (alive) setLoaded(true);
      }
    };
    init();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setTimezone = useCallback((tz) => {
    setPrefs((prev) => { const next = { ...prev, timezone: tz || defaultPrefs.timezone }; persist(next); return next; });
  }, [persist]);
  const setLocale = useCallback((loc) => {
    setPrefs((prev) => { const next = { ...prev, locale: loc || defaultPrefs.locale }; persist(next); return next; });
  }, [persist]);
  const setCurrency = useCallback((curr) => {
    setPrefs((prev) => { const next = { ...prev, currency: (curr || defaultPrefs.currency).toString().toUpperCase() }; persist(next); return next; });
  }, [persist]);
  const setDefaultAccountId = useCallback((id) => {
    setPrefs((prev) => { const next = { ...prev, defaultAccountId: id ?? null }; persist(next); return next; });
  }, [persist]);

  const formatDate = useCallback((value, options) => {
    const d = parseDateLike(value);
    if (!d) return '';
    try { return new Intl.DateTimeFormat(prefs.locale, { timeZone: prefs.timezone, ...options }).format(d); }
    catch { return d.toISOString(); }
  }, [prefs.locale, prefs.timezone]);

  const formatDateTime = useCallback((value, options) => {
    const d = parseDateLike(value);
    if (!d) return '';
    const base = { year: 'numeric', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' };
    try { return new Intl.DateTimeFormat(prefs.locale, { timeZone: prefs.timezone, ...base, ...(options || {}) }).format(d); }
    catch { return d.toISOString(); }
  }, [prefs.locale, prefs.timezone]);

  const formatNumber = useCallback((num, options) => {
    const n = Number(num);
    if (!Number.isFinite(n)) return '';
    try { return new Intl.NumberFormat(prefs.locale, options).format(n); }
    catch { return String(n); }
  }, [prefs.locale]);

  const formatCurrency = useCallback((amount, currency) => {
    const n = Number(amount);
    if (!Number.isFinite(n)) return '';
    const curr = (currency || prefs.currency || 'USD').toUpperCase();
    try { return new Intl.NumberFormat(prefs.locale, { style: 'currency', currency: curr, currencyDisplay: 'narrowSymbol' }).format(n); }
    catch { return `${curr} ${n.toFixed(2)}`; }
  }, [prefs.locale, prefs.currency]);

  const value = useMemo(() => ({
    loaded,
    timezone: prefs.timezone,
    locale: prefs.locale,
    currency: prefs.currency,
  defaultAccountId: prefs.defaultAccountId,
    setTimezone,
    setLocale,
    setCurrency,
  setDefaultAccountId,
    formatDate,
    formatDateTime,
    formatNumber,
    formatCurrency,
  }), [loaded, prefs, setTimezone, setLocale, setCurrency, setDefaultAccountId, formatDate, formatDateTime, formatNumber, formatCurrency]);

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
}

export const useUserPreferences = () => useContext(UserPreferencesContext);
